import { format, toZonedTime } from 'date-fns-tz'

import {
  AuctionViewing,
  LegacyDateValue,
} from '@bonhams1793/bonhams-typescript'

import { FireButton } from '@nx/fire/button'
import { FireLink } from '@nx/fire/link'
import { Body } from '@nx/fire/typography'
import { buildAuctionPath, buildLocationPath } from '@nx/helpers'
import { SmartlingAttributes } from '@nx/smartling'

import { StyledAuctionSection } from './FireAuctionViewings.styles'
import {
  auctionViewingsAuctionListMapper,
  auctionViewingsLocationListMapper,
} from './auctionViewingsMappers'

const buildAuctionViewingDate = (
  start: LegacyDateValue,
  end: LegacyDateValue
) => {
  const startDate = toZonedTime(start.datetime, start.timezone.iana)
  const endDate = toZonedTime(end.datetime, end.timezone.iana)

  return `${format(startDate, 'd LLLL yyyy', {
    timeZone: start.timezone.iana,
  })}, ${format(startDate, 'HH:mm', {
    timeZone: start.timezone.iana,
  })} - ${format(endDate, 'HH:mm', {
    timeZone: end.timezone.iana,
  })} ${start.timezone.abbreviation}`
}

const mapAuctionViewings = (
  auctionViewings: AuctionViewing[],
  groupBy: FireAuctionViewingsProps['groupBy']
) => {
  if (groupBy === 'auction') {
    return auctionViewingsAuctionListMapper(auctionViewings)
  } else {
    return auctionViewingsLocationListMapper(auctionViewings)
  }
}

const buildPath = (
  entity: AuctionViewing['auction'] | NonNullable<AuctionViewing['location']>,
  domain?: string
) => {
  if ('biddingEngine' in entity) {
    return buildAuctionPath({
      auctionId: entity.id,
      biddingEngine: entity.biddingEngine,
      domain,
      slug: entity.slug,
    })
  } else {
    return buildLocationPath(entity.code, entity.name, domain)
  }
}

interface FireAuctionViewingsProps {
  auctionViewings: AuctionViewing[]
  groupBy?: 'auction' | 'location'
  groupNameComponent?: 'button' | 'link'
  domain?: string
}

export function FireAuctionViewings({
  auctionViewings,
  groupBy = 'auction',
  groupNameComponent = 'button',
  domain,
}: FireAuctionViewingsProps) {
  return (
    <div>
      {mapAuctionViewings(auctionViewings, groupBy).map(
        ({ entity, viewingDays }, i) => (
          <StyledAuctionSection
            key={`group-${i}`}
            {...SmartlingAttributes.noTranslate}
          >
            {groupNameComponent === 'button' ? (
              <FireButton variant="label" href={buildPath(entity, domain)}>
                {entity.name}
              </FireButton>
            ) : (
              <FireLink href={buildPath(entity, domain)}>
                {entity.name}
              </FireLink>
            )}
            {viewingDays.map(({ id, start, end }) => (
              <Body key={id}>{buildAuctionViewingDate(start, end)}</Body>
            ))}
          </StyledAuctionSection>
        )
      )}
    </div>
  )
}
